/**
 * ManagedCompanySettings - Configurações de uma empresa gerenciada
 * Inclui: Features, Importação de dados
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Settings,
  Building2,
  Loader2,
  Save,
  ArrowLeft,
  Download,
  FileText,
  Users,
  GraduationCap,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

// Lista de features disponíveis
const AVAILABLE_FEATURES = {
  selecao_aberta: { nome: 'Seleção Aberta', descricao: 'Publicar vagas e receber candidaturas' },
  avaliacao_desempenho: { nome: 'Avaliação de Desempenho', descricao: 'Ciclos de avaliação e feedback' },
  testes_comportamentais: { nome: 'Testes Comportamentais', descricao: 'DISC, Big Five e outros testes' },
  grupos_testes: { nome: 'Grupos de Testes', descricao: 'Aplicação de testes em lote' },
  treinamentos: { nome: 'Treinamentos', descricao: 'Gestão de treinamentos e capacitações' },
  pdi_pdc: { nome: 'PDI/PDC', descricao: 'Planos de desenvolvimento individual e coletivo' },
  clima_nr01: { nome: 'Clima e NR01', descricao: 'Pesquisa de clima e diagnóstico NR01' }
};

// Tipos de dados para importação
const IMPORT_TYPES = [
  { key: 'cargos', nome: 'Descrições de Cargos', icon: FileText, descricao: 'Importar descrições e requisitos de cargos' },
  { key: 'setores', nome: 'Setores/Departamentos', icon: Users, descricao: 'Importar lista de setores e departamentos' },
  { key: 'grades_treinamento', nome: 'Grades de Treinamento', icon: GraduationCap, descricao: 'Importar grades e trilhas de aprendizado' }
];

export default function ManagedCompanySettings() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [features, setFeatures] = useState({});
  const [herdarPrincipal, setHerdarPrincipal] = useState(true);
  
  // Estado para importação
  const [importType, setImportType] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getManagedFeatures(companyId);
      setData(response.data);
      setFeatures(response.data.features || {});
      setHerdarPrincipal(response.data.herdar_principal !== false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFeatures = async () => {
    setSaving(true);
    try {
      await companiesAPI.updateManagedFeatures(companyId, {
        herdar_principal: herdarPrincipal,
        features: herdarPrincipal ? null : features
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenImport = async (type) => {
    setImportType(type);
    setShowImportDialog(true);
    setLoadingPreview(true);
    
    try {
      const response = await companiesAPI.getImportPreview(companyId, type);
      setImportPreview(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar preview');
      setImportPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!importType) return;
    
    setImporting(true);
    try {
      const response = await companiesAPI.importData(companyId, importType);
      toast.success(`${response.data.importados} itens importados com sucesso!`);
      setShowImportDialog(false);
      setImportPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importação');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/company/settings')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Building2 className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{data?.nome}</h1>
              <p className="text-slate-500">Configurações da Empresa Gerenciada</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-600" />
              Recursos Habilitados
            </CardTitle>
            <CardDescription>
              Configure quais recursos estão disponíveis para esta empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Toggle herdar da principal */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
              <div className="space-y-1">
                <Label className="text-base font-medium">Herdar da Empresa Principal</Label>
                <p className="text-sm text-slate-500">
                  Usar os mesmos recursos habilitados na empresa principal
                </p>
              </div>
              <Switch 
                checked={herdarPrincipal} 
                onCheckedChange={setHerdarPrincipal}
              />
            </div>

            {!herdarPrincipal && (
              <>
                <Separator />
                <div className="space-y-4">
                  <Label className="text-sm font-semibold text-slate-700">Recursos Individuais</Label>
                  {Object.entries(AVAILABLE_FEATURES).map(([key, feature]) => (
                    <div 
                      key={key}
                      className="flex items-center justify-between py-3 border-b border-slate-100"
                    >
                      <div className="space-y-1">
                        <Label className="font-medium">{feature.nome}</Label>
                        <p className="text-xs text-slate-500">{feature.descricao}</p>
                      </div>
                      <Switch 
                        checked={features[key] || false}
                        onCheckedChange={(checked) => setFeatures({ ...features, [key]: checked })}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            {herdarPrincipal && data?.features_principal && (
              <>
                <Separator />
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-slate-700">Recursos da Empresa Principal</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.features_principal).map(([key, enabled]) => {
                      const feature = AVAILABLE_FEATURES[key];
                      if (!feature) return null;
                      return (
                        <Badge 
                          key={key}
                          variant={enabled ? "default" : "secondary"}
                          className={enabled ? "bg-cyan-100 text-cyan-700" : ""}
                        >
                          {enabled ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                          {feature.nome}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveFeatures} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar Configurações
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Importação de Dados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-cyan-600" />
              Importar da Empresa Principal
            </CardTitle>
            <CardDescription>
              Copie dados configurados na empresa principal para esta empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {IMPORT_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <Card 
                    key={type.key}
                    className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 hover:border-cyan-300"
                    onClick={() => handleOpenImport(type.key)}
                  >
                    <CardContent className="pt-6 text-center">
                      <div className="mx-auto w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center mb-3">
                        <Icon className="w-6 h-6 text-cyan-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{type.nome}</h3>
                      <p className="text-xs text-slate-500 mt-1">{type.descricao}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Dialog de Importação */}
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-cyan-600" />
                Importar {IMPORT_TYPES.find(t => t.key === importType)?.nome}
              </DialogTitle>
              <DialogDescription>
                De: {importPreview?.empresa_origem} → Para: {importPreview?.empresa_destino}
              </DialogDescription>
            </DialogHeader>
            
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
              </div>
            ) : importPreview ? (
              <div className="space-y-4">
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{importPreview.total_disponivel}</p>
                    <p className="text-xs text-slate-500">Disponíveis</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{importPreview.ja_existentes}</p>
                    <p className="text-xs text-slate-500">Já existem</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{importPreview.novos}</p>
                    <p className="text-xs text-slate-500">Serão importados</p>
                  </div>
                </div>

                {/* Lista de itens */}
                {importPreview.itens?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    {importPreview.itens.map((item, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between px-3 py-2 border-b last:border-0 ${
                          item.ja_existe ? 'bg-slate-50 text-slate-400' : ''
                        }`}
                      >
                        <span className="text-sm">{item.titulo}</span>
                        {item.ja_existe ? (
                          <Badge variant="secondary" className="text-xs">Já existe</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 text-xs">Novo</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {importPreview.novos === 0 && (
                  <div className="text-center py-4 text-amber-600">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                    <p>Todos os itens já existem nesta empresa</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Nenhum dado para importar
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleImport}
                disabled={importing || !importPreview?.novos}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Importar {importPreview?.novos || 0} itens
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
